import { cn } from "@headerhawk/ui";
import type { UsageStatus } from "@/lib/api/client";
import { formatReportCount, formatLimit } from "@/lib/hooks/use-usage";

export interface UsageMeterProps {
  reportCount: number;
  limit: number | null;
  percentUsed: number;
  periodEnd: string;
  status: UsageStatus;
  compact?: boolean;
}

/**
 * Get color classes based on usage status
 */
function getStatusColors(status: UsageStatus) {
  switch (status) {
    case "under":
      return {
        bar: "bg-green-500",
        text: "text-green-600 dark:text-green-400",
      };
    case "warning":
      return {
        bar: "bg-yellow-500",
        text: "text-yellow-600 dark:text-yellow-400",
      };
    case "soft_exceeded":
      return {
        bar: "bg-orange-500",
        text: "text-orange-600 dark:text-orange-400",
      };
    case "hard_exceeded":
      return {
        bar: "bg-red-500",
        text: "text-red-600 dark:text-red-400",
      };
  }
}

/**
 * Get status icon based on usage status
 */
function getStatusIcon(status: UsageStatus): string {
  switch (status) {
    case "under":
      return "";
    case "warning":
      return "";
    case "soft_exceeded":
      return "";
    case "hard_exceeded":
      return "";
  }
}

/**
 * Format the period end date for display
 */
function formatResetDate(periodEnd: string): string {
  const date = new Date(periodEnd);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function UsageMeter({
  reportCount,
  limit,
  percentUsed,
  periodEnd,
  status,
  compact = false,
}: UsageMeterProps) {
  const colors = getStatusColors(status);
  const statusIcon = getStatusIcon(status);

  // Cap visual percentage at 100% for the bar display
  const displayPercent = Math.min(percentUsed, 100);

  // For unlimited plans
  const isUnlimited = limit === null;

  const usageText = isUnlimited
    ? `${formatReportCount(reportCount)} reports`
    : `${formatReportCount(reportCount)} / ${formatLimit(limit)}`;

  if (compact) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Usage</span>
          <span className={cn("font-medium", colors.text)}>
            {statusIcon}
            {isUnlimited ? "" : `${percentUsed}%`}
          </span>
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={percentUsed}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Usage: ${usageText}`}
        >
          {!isUnlimited && (
            <div
              className={cn("h-full rounded-full transition-all", colors.bar)}
              style={{ width: `${displayPercent}%` }}
            />
          )}
        </div>
        <div className="text-xs text-muted-foreground">{usageText}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Monthly Usage</span>
        <span className={cn("text-sm font-medium", colors.text)}>
          {statusIcon}
          {isUnlimited ? "Unlimited" : `${percentUsed}%`}
        </span>
      </div>

      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percentUsed}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Monthly usage: ${usageText}`}
      >
        {!isUnlimited && (
          <div
            className={cn("h-full rounded-full transition-all", colors.bar)}
            style={{ width: `${displayPercent}%` }}
          />
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{usageText}</span>
        <span>Resets {formatResetDate(periodEnd)}</span>
      </div>
    </div>
  );
}
